import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { CompsView } from './CompsView'
import { WeeklySheetView } from './WeeklySheetView'
import { AccommodationView } from './AccommodationView'
import { CourseSalesView } from './CourseSalesView'
import { reportPageView } from '../pageViews'

// Customer Service tab — lookups switched by a segmented toggle:
//   • Members directory        (cache.MembersDirectory,     /api/customer-service/members)
//   • Gift card write-downs     (cache.GiftCardWriteDowns,   /api/customer-service/gift-card-writedowns)
//   • Guard sheet               (cache.GuardSheet,           /api/customer-service/guard-sheet)
//   • Comps                     (cache.CompUsage/CompPeople, /api/customer-service/comps) — CompsView.tsx
//   • Weekly sheet              (Info Hub Weekly Sheet via /api/customer-service/weekly-sheet) — WeeklySheetView.tsx
//   • Accommodation             (cache.AccommodationBookings, /api/customer-service/accommodation) — AccommodationView.tsx
//   • Course Sales              (cache.CourseBookings + CourseSessions, /api/customer-service/courses) — CourseSalesView.tsx
// Both are visible to every signed-in report user. Each has its own text box that
// searches EVERY column server-side; results are capped (the tables are large) with
// the total match count shown.

interface MemberRow {
  MembershipNumber: number
  Name: string | null
  Address: string | null
  Tel: string | null
  Email: string | null
  MembershipType: string | null
  LastRenewed: string | null
  CardNo: string | null
  MembershipStatus: string | null
}
interface GiftCardRow {
  GiftCardNo: string | null
  CurrentBalance: string | null
  ActionType: string | null
  PreviousDate: string | null
  ActionDate: string | null
  Amount: number | null
  CustomerName: string | null
  PostCode: string | null
  ShortText: string | null
  LongText: string | null
  Operator: string | null
  TillNo: number | null
  TxnNo: number | null
  TxnSeq: number | null
  TransactionTime: string | null
}
interface GuardSheetRow {
  ID: string | null; ColourCode: string | null; JourneyDay: string | null; JourneyDate: string | null
  JourneyDMY: string | null; JourneyTime: string | null; TicketDetails: string | null; Direction: string | null
  DirectionRoute: string | null; DirectionSeq: string | null; FromStation: string | null; ToStation: string | null
  SessionFrom: string | null; ToStationNo: string | null; Adults: string | null; SessionTo: string | null
  FromStationNo: string | null; Children: string | null; Booking: string | null; Total: string | null
  AccountName: string | null; Contact: string | null; Financial: string | null; Notes: string | null
}
interface Payload<T> {
  rows: T[]
  total: number
  page_size: number
  generated_at: string | null
  directory_rows: number
}

const TH: CSSProperties = { textAlign: 'left', padding: '6px 8px', color: '#6b6b6b', fontWeight: 500, fontSize: '0.78rem', borderBottom: '1px solid #e5e1d6', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff' }
const TD: CSSProperties = { padding: '6px 8px', fontSize: '0.8rem', borderBottom: '1px solid #f0ece1', verticalAlign: 'top' }
const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 })

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso + 'T00:00:00')
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
}

// Debounced server-side search shared by both lookups.
function useSearch<T>(path: string, q: string) {
  const [data, setData] = useState<Payload<T> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const debounce = useRef<number | null>(null)
  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current)
    debounce.current = window.setTimeout(() => {
      let live = true
      setLoading(true)
      fetch(`${path}?q=${encodeURIComponent(q)}`)
        .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<Payload<T>> })
        .then((d) => { if (live) { setData(d); setError(null) } })
        .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)) })
        .finally(() => { if (live) setLoading(false) })
      return () => { live = false }
    }, 350)
    return () => { if (debounce.current) window.clearTimeout(debounce.current) }
  }, [path, q])
  return { data, loading, error }
}

function SearchBar({ q, setQ, placeholder, loading, total, shown }: { q: string; setQ: (v: string) => void; placeholder: string; loading: boolean; total: number; shown: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        style={{ flex: '1 1 380px', maxWidth: 560, padding: '8px 12px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 8 }}
        aria-label={placeholder}
      />
      <span style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>
        {loading ? 'Searching…' : `${total.toLocaleString('en-GB')} match${total === 1 ? '' : 'es'}${total > shown ? ` — showing first ${shown}, refine your search` : ''}`}
      </span>
    </div>
  )
}

function MembersView() {
  const [q, setQ] = useState('')
  const { data, loading, error } = useSearch<MemberRow>('/api/customer-service/members', q)
  const rows = data?.rows ?? []
  const empty = !loading && !error && (data?.directory_rows ?? 0) === 0
  return (
    <>
      <SearchBar q={q} setQ={setQ} loading={loading} total={data?.total ?? 0} shown={rows.length}
        placeholder="Search name, address, phone, email, membership no, type, card no, status…" />
      {error && <div className="placeholder" style={{ borderColor: '#a33' }}><strong>Couldn&apos;t load members</strong><p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p></div>}
      {empty ? (
        <div className="placeholder"><strong>Awaiting first refresh</strong><p>The members directory is empty — run <code>Refresh-MemberEmails.ps1</code> on ONTRACK to populate it.</p></div>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={TH}>Membership No</th><th style={TH}>Name</th><th style={TH}>Address</th><th style={TH}>Tel</th>
                <th style={TH}>Email</th><th style={TH}>Type</th><th style={TH}>Last renewed</th><th style={TH}>Card No</th><th style={TH}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.MembershipNumber}-${r.CardNo ?? i}`}>
                  <td style={TD}>{r.MembershipNumber}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.Name ?? '—'}</td>
                  <td style={TD}>{r.Address ?? '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.Tel ?? '—'}</td>
                  <td style={TD}>{r.Email ?? '—'}</td>
                  <td style={TD}>{r.MembershipType ?? '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDate(r.LastRenewed)}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.CardNo ?? '—'}</td>
                  <td style={TD}>{r.MembershipStatus ?? '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && <tr><td style={TD} colSpan={9}>No members match &ldquo;{q}&rdquo;.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function GiftCardWriteDownsView() {
  const [q, setQ] = useState('')
  const { data, loading, error } = useSearch<GiftCardRow>('/api/customer-service/gift-card-writedowns', q)
  const rows = data?.rows ?? []
  const empty = !loading && !error && (data?.directory_rows ?? 0) === 0
  return (
    <>
      <SearchBar q={q} setQ={setQ} loading={loading} total={data?.total ?? 0} shown={rows.length}
        placeholder="Search gift card no, action, customer, postcode, operator, txn no, text…" />
      {error && <div className="placeholder" style={{ borderColor: '#a33' }}><strong>Couldn&apos;t load gift-card write-downs</strong><p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p></div>}
      {empty ? (
        <div className="placeholder"><strong>Awaiting first refresh</strong><p>The gift-card write-downs cache is empty — run <code>Refresh-GiftCardWriteDowns.ps1</code> on ONTRACK to populate it.</p></div>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1200 }}>
            <thead>
              <tr>
                <th style={TH}>Gift Card No</th><th style={TH}>Current Balance</th><th style={TH}>Action Type</th>
                <th style={TH}>Previous Date</th><th style={TH}>Action Date</th><th style={{ ...TH, textAlign: 'right' }}>Amount</th>
                <th style={TH}>Customer Name</th><th style={TH}>Post Code</th><th style={TH}>Short Text</th><th style={TH}>Long Text</th>
                <th style={TH}>Operator</th><th style={{ ...TH, textAlign: 'right' }}>Till No</th><th style={{ ...TH, textAlign: 'right' }}>Txn No</th>
                <th style={{ ...TH, textAlign: 'right' }}>Txn Seq</th><th style={TH}>Transaction Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.GiftCardNo}-${r.TxnNo ?? i}-${r.TxnSeq ?? i}-${i}`}>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.GiftCardNo ?? '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.CurrentBalance ? r.CurrentBalance : '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.ActionType ?? '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.PreviousDate ? r.PreviousDate : '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.ActionDate ? r.ActionDate : '—'}</td>
                  <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap', color: (r.Amount ?? 0) < 0 ? '#c0392b' : '#0b3d2e', fontVariantNumeric: 'tabular-nums' }}>{r.Amount == null ? '—' : gbp.format(r.Amount)}</td>
                  <td style={TD}>{r.CustomerName ?? '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.PostCode ?? '—'}</td>
                  <td style={TD}>{r.ShortText ?? '—'}</td>
                  <td style={TD}>{r.LongText ?? '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.Operator ?? '—'}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{r.TillNo ?? '—'}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{r.TxnNo ?? '—'}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{r.TxnSeq ?? '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDateTime(r.TransactionTime)}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && <tr><td style={TD} colSpan={15}>No gift-card actions match &ldquo;{q}&rdquo;.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

interface GuardSheetPayload extends Payload<GuardSheetRow> { window_from: string | null; window_to: string | null }

function isoToday(offsetDays = 0): string {
  const d = new Date(); d.setDate(d.getDate() + offsetDays); return d.toISOString().slice(0, 10)
}

function GuardSheetView() {
  const [from, setFrom] = useState(isoToday(0))
  const [to, setTo] = useState(isoToday(7))
  const [q, setQ] = useState('')
  const [data, setData] = useState<GuardSheetPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const debounce = useRef<number | null>(null)

  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current)
    debounce.current = window.setTimeout(() => {
      let live = true
      setLoading(true)
      const qs = new URLSearchParams({ from, to, q }).toString()
      fetch(`/api/customer-service/guard-sheet?${qs}`)
        .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<GuardSheetPayload> })
        .then((d) => { if (live) { setData(d); setError(null) } })
        .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)) })
        .finally(() => { if (live) setLoading(false) })
      return () => { live = false }
    }, 350)
    return () => { if (debounce.current) window.clearTimeout(debounce.current) }
  }, [from, to, q])

  const rows = data?.rows ?? []
  const empty = !loading && !error && (data?.directory_rows ?? 0) === 0
  const label: CSSProperties = { fontSize: '0.8rem', color: '#6b6b6b', display: 'flex', flexDirection: 'column', gap: 2 }
  const dateInput: CSSProperties = { padding: '6px 10px', fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: 8 }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <label style={label}>From<input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={dateInput} /></label>
        <label style={label}>To<input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={dateInput} /></label>
        <input
          type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search station, direction, account, contact, booking, notes…"
          style={{ flex: '1 1 320px', maxWidth: 460, padding: '8px 12px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 8 }}
          aria-label="Search guard sheet"
        />
        <span style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>
          {loading ? 'Loading…' : `${(data?.total ?? 0).toLocaleString('en-GB')} journey${(data?.total ?? 0) === 1 ? '' : 's'}${(data?.total ?? 0) > rows.length ? ` — showing first ${rows.length}, narrow the dates` : ''}`}
        </span>
      </div>

      {error && <div className="placeholder" style={{ borderColor: '#a33' }}><strong>Couldn&apos;t load the guard sheet</strong><p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p></div>}
      {empty ? (
        <div className="placeholder"><strong>Awaiting first refresh</strong><p>The guard sheet cache is empty — run <code>Refresh-GuardSheet.ps1</code> on ONTRACK to populate it.</p></div>
      ) : (
        <>
          {data?.window_from && data?.window_to && (
            <p style={{ fontSize: '0.75rem', color: '#8a8577', margin: '0 0 8px' }}>Cached journeys available {fmtDate(data.window_from)} – {fmtDate(data.window_to)}.</p>
          )}
          <div style={{ overflowX: 'auto', maxHeight: '68vh', overflowY: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1300 }}>
              <thead>
                <tr>
                  <th style={TH}>Date</th><th style={TH}>Day</th><th style={TH}>Time</th><th style={TH}>Direction</th>
                  <th style={TH}>From</th><th style={TH}>To</th><th style={TH}>Ticket details</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Adults</th><th style={{ ...TH, textAlign: 'right' }}>Children</th><th style={{ ...TH, textAlign: 'right' }}>Total</th>
                  <th style={TH}>Booking</th><th style={TH}>Account</th><th style={TH}>Contact</th><th style={TH}>Financial</th><th style={TH}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.Booking ?? ''}-${r.JourneyDate ?? ''}-${r.JourneyTime ?? ''}-${i}`}>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.JourneyDate ? fmtDate(r.JourneyDate) : '—'}</td>
                    <td style={TD}>{r.JourneyDay ?? '—'}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.JourneyTime ?? '—'}</td>
                    <td style={TD}>{r.Direction ?? '—'}</td>
                    <td style={TD}>{r.FromStation ?? '—'}</td>
                    <td style={TD}>{r.ToStation ?? '—'}</td>
                    <td style={TD}>{r.TicketDetails ?? '—'}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{r.Adults ?? '—'}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{r.Children ?? '—'}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{r.Total ?? '—'}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.Booking ?? '—'}</td>
                    <td style={TD}>{r.AccountName ?? '—'}</td>
                    <td style={TD}>{r.Contact ?? '—'}</td>
                    <td style={TD}>{r.Financial ?? '—'}</td>
                    <td style={TD}>{r.Notes ?? '—'}</td>
                  </tr>
                ))}
                {rows.length === 0 && !loading && <tr><td style={TD} colSpan={15}>No journeys in {fmtDate(from)} – {fmtDate(to)}{q ? ` matching “${q}”` : ''}.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}

type CsView = 'members' | 'giftcards' | 'guardsheet' | 'comps' | 'weeklysheet' | 'accommodation' | 'courses'

export function CustomerServiceScreen() {
  const [view, setView] = useState<CsView>('members')
  // Admin -> Viewing (db/112): report the sub-view so "Customer Service › Comps" is distinct.
  useEffect(() => { reportPageView('/customer-service', view) }, [view])

  const pill = (v: CsView, label: string) => (
    <button
      type="button"
      onClick={() => setView(v)}
      style={{
        background: view === v ? '#0b3d2e' : '#fff', color: view === v ? '#fff' : '#0b3d2e',
        border: '1px solid #0b3d2e', borderRadius: 6, padding: '5px 16px', cursor: 'pointer',
        fontWeight: 700, fontSize: '0.82rem',
      }}
      aria-pressed={view === v}
    >{label}</button>
  )

  return (
    <main className="app-main">
      <div className="budget-toolbar">
        <div>
          <p className="budget-sub">
            {view === 'members'
              ? 'Members — current members from the Ontrack membership system.'
              : view === 'giftcards'
                ? 'Gift card write-downs — every gift-card action (bought, used, refund/top-up, and the automatic write-down after the 2-year expiry), from MERAC.'
                : view === 'guardsheet'
                  ? 'Guard sheet — the booking manifest for services in a date range (journey, direction, passengers, account, notes), from MERAC.'
                  : view === 'comps'
                    ? 'Comps — complimentary tickets issued to ID-card holders (staff, volunteers, associates) by season, from the MERAC till lines, matched to OnTrack ID cards and Info Hub users.'
                    : view === 'weeklysheet'
                      ? 'Weekly sheet — the Info Hub Weekly Sheet (group bookings, dining and event services by day, standard-service capacity, DM duty) for last, this and next week, with PDF and XLS downloads.'
                      : view === 'accommodation'
                        ? 'Accommodation — Goathland and Levisham Camping Coach and Grosmont Station House lets from MERAC: week-by-property calendar (Friday to Friday), arrivals with guests, dog, key code and paid so far, season totals with Compare to last season, PDF and XLS. Refreshed nightly 05:30.'
                        : 'Course Sales — Steam / Diesel / Mixed-Traction Footplate, Steam / Diesel Driver and Signaller experience bookings from MERAC: upcoming dates with places sold against capacity, season totals by experience with Compare to last season, unpaid balances, PDF and XLS. Refreshed nightly 05:30.'}
          </p>
        </div>
      </div>

      <div style={{ display: 'inline-flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }} role="group" aria-label="Customer Service view">
        {pill('members', 'Members')}
        {pill('giftcards', 'Gift card write-downs')}
        {pill('guardsheet', 'Guard sheet')}
        {pill('comps', 'Comps')}
        {pill('weeklysheet', 'Weekly sheet')}
        {pill('accommodation', 'Accommodation')}
        {pill('courses', 'Course Sales')}
      </div>

      {view === 'members' ? <MembersView /> : view === 'giftcards' ? <GiftCardWriteDownsView /> : view === 'guardsheet' ? <GuardSheetView /> : view === 'comps' ? <CompsView /> : view === 'weeklysheet' ? <WeeklySheetView /> : view === 'accommodation' ? <AccommodationView /> : <CourseSalesView />}
    </main>
  )
}
